import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import { useModalBottomPadding } from '../../src/utils/useModalBottomPadding';
import type { ColorTheme } from '../../src/utils/theme';
import { titleCase } from '../../src/utils/format';

interface ShiftItem {
  readonly id: string;
  readonly attendantName: string;
  readonly locationName: string;
  readonly clockInTime: string;
  readonly clockOutTime: string | null;
  readonly durationMinutes: number | null;
  readonly status: string;
  readonly notes?: string;
  readonly createdAt: string;
}

/** Status filter options for the shift list toolbar */
const STATUS_FILTERS = [
  { label: 'Active', value: 'active' },
  { label: 'Completed', value: 'completed' },
  { label: 'Abandoned', value: 'abandoned' },
];

/** Maps shift status to a display color */
const statusColor = (s: string) => {
  if (s === 'active') return '#059669';
  if (s === 'completed') return '#2563eb';
  return '#d97706';
};

/** Page size for shift list pagination */
const PAGE_LIMIT = 50;

/** Minutes per hour for duration formatting */
const MINUTES_PER_HOUR = 60;

/** AdminShifts - lists and filters attendant shifts with status badges */
export default function AdminShifts() {
  const { attendantId: paramAttendantId } = useLocalSearchParams<{ attendantId?: string }>();
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [shifts, setShifts] = useState<ShiftItem[]>([]);
  const [selectedShift, setSelectedShift] = useState<ShiftItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [attendantName, setAttendantName] = useState('');

  const fetchData = useCallback(async (p = 1, s = '', status = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      /* Fetch paginated shifts with optional search/status filters */
      const params = new URLSearchParams({ page: String(p), limit: String(PAGE_LIMIT) });
      if (s) params.set('search', s);
      if (status) params.set('status', status);
      if (paramAttendantId) params.set('attendantId', paramAttendantId);
      const data = await api<{ shifts: ShiftItem[]; total: number }>(`/api/admin/attendants/shifts?${params}`);
      if (signal?.aborted) return;
      setShifts(data.shifts);
      setTotal(data.total);
      setPage(p);
      if (paramAttendantId && data.shifts.length > 0 && !attendantName) {
        setAttendantName(data.shifts[0].attendantName);
      }
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, [paramAttendantId, attendantName]);

  useEffect(() => { const ac = new AbortController(); fetchData(1, '', '', ac.signal); return () => ac.abort(); }, [paramAttendantId]);

  const csvEndpoint = `/api/admin/attendants/shifts?${new URLSearchParams({ ...(search && { search }), ...(statusFilter && { status: statusFilter }) })}`;

  /** Formats shift duration as human-readable string */
  const fmtDuration = (mins: number | null) => {
    if (mins === null) return 'In progress';
    const h = Math.floor(mins / MINUTES_PER_HOUR);
    const m = mins % MINUTES_PER_HOUR;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatDateTime = (iso: string) => new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });

  const renderItem = ({ item }: { item: ShiftItem }) => (
    <TouchableOpacity style={styles.row} onPress={() => setSelectedShift(item)}>
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={styles.name}>{titleCase(item.attendantName)}</Text>
          <View style={[styles.badge, { backgroundColor: statusColor(item.status) + '15' }]}>
            <Text style={[styles.badgeText, { color: statusColor(item.status) }]}>{item.status}</Text>
          </View>
        </View>
        <Text style={styles.meta}>
          {item.locationName} · {new Date(item.clockInTime).toLocaleString()}
          {item.clockOutTime ? ` — ${new Date(item.clockOutTime).toLocaleTimeString()}` : ''}
        </Text>
        <Text style={styles.sub}>Duration: {fmtDuration(item.durationMinutes)}</Text>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      {paramAttendantId && (
        <View style={styles.filterBanner}>
          <Text style={styles.filterText}>Shifts for: {titleCase(attendantName) || 'Attendant'}</Text>
          <TouchableOpacity onPress={() => router.replace('/admin/shifts' as never)} hitSlop={8}>
            <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>
      )}
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={() => fetchData(1, search, statusFilter)}
        total={total} page={page} limit={PAGE_LIMIT} onPageChange={(p) => fetchData(p, search, statusFilter)}
        csvEndpoint={csvEndpoint} csvFilename="shifts.csv" label="shift"
        filterOptions={STATUS_FILTERS} activeFilter={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); fetchData(1, search, v); }}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />} data={shifts} keyExtractor={item => item.id} renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No shifts found</Text>}
        />
      )}

      {/* Shift Detail Modal */}
      <Modal visible={!!selectedShift} animationType="slide" transparent onRequestClose={() => setSelectedShift(null)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setSelectedShift(null)}>
          <View style={[styles.modalContent, { paddingBottom: modalBottomPadding + spacing.lg }]} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Shift Details</Text>
              <TouchableOpacity onPress={() => setSelectedShift(null)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            {selectedShift && (
              <ScrollView>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Attendant</Text>
                  <Text style={styles.detailValue}>{titleCase(selectedShift.attendantName)}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Location</Text>
                  <Text style={styles.detailValue}>{selectedShift.locationName}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Status</Text>
                  <View style={[styles.badge, { backgroundColor: statusColor(selectedShift.status) + '15' }]}>
                    <Text style={[styles.badgeText, { color: statusColor(selectedShift.status) }]}>
                      {selectedShift.status === 'active' ? 'Active' : selectedShift.status === 'completed' ? 'Completed' : 'Abandoned'}
                    </Text>
                  </View>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Clock In</Text>
                  <Text style={styles.detailValue}>{formatDateTime(selectedShift.clockInTime)}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Clock Out</Text>
                  <Text style={styles.detailValue}>{selectedShift.clockOutTime ? formatDateTime(selectedShift.clockOutTime) : 'Still active'}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Duration</Text>
                  <Text style={styles.detailValue}>{fmtDuration(selectedShift.durationMinutes)}</Text>
                </View>
                {selectedShift.notes ? (
                  <View style={[styles.detailRow, { flexDirection: 'column', alignItems: 'flex-start' }]}>
                    <Text style={styles.detailLabel}>Notes</Text>
                    <Text style={[styles.detailValue, { textAlign: 'left', marginLeft: 0, marginTop: spacing.xs }]}>{selectedShift.notes}</Text>
                  </View>
                ) : null}
              </ScrollView>
            )}
          </View>
        </TouchableOpacity>
      </Modal>
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  filterBanner: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: 8, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, marginBottom: spacing.sm },
  filterText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  row: { paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  name: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 8 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  meta: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  sub: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' },
  modalContent: { backgroundColor: c.surface, borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: spacing.lg, maxHeight: '70%' },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.md },
  modalTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: c.text },
  detailRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  detailLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  detailValue: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, textAlign: 'right' as const, flex: 1, marginLeft: spacing.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
